import React, { useEffect, useRef, useState } from "react"
import ReactDOM from 'react-dom'
import Icon from '../atoms/Icon'
import Video from "../atoms/Video"
import Image from "../atoms/Image"
import useSiteData from "../../useSiteData"
import useWindowSize from "../util/useWindowSize"

const GalleryItemBody = ( {item} ) => {
  return (
  <>
  { (item.title || item.description) &&
    <div className="o-modal-gallery__item-body">
    { item.title &&
      <h4>{item.title}</h4>
    }
    {
      item.description &&
      <p>{item.description}</p>
    }
    </div>
  }
  </>
  )
}

// here we set data-index so we can use the item in the gallery when calculating if the image fits vertically on screen without waiting for
// images to be loaded
const buildModalGalleryItems = ( gallery, metaDataLoadedCallback=null ) => {
  
  const content = gallery.items.map(( item, index) => {

    switch( item.file.contentType.substr(0, 5) ) {

      case "image" :
        return (
          <div className="o-modal-gallery__item o-modal-gallery__item--image" key={item.assetId}>
            <div className="o-modal-gallery__item--image-wrapper" data-index={ index }>
              <Image image={ item } />
            </div>
            { gallery.showItemsTitleDescription && 
              <GalleryItemBody item={ item }/>
            }
          </div>
        )

      case "video":
        return (
          <div className="o-modal-gallery__item o-modal-gallery__item--video" key={item.assetId}>
            <div className="o-modal-gallery__item--video-wrapper">
              <Video src={ item.file.url } className="u-object-fit__media" playButton={ true } withControls={ true } onMetaData={ metaDataLoadedCallback }/>
            </div>
            { gallery.showItemsTitleDescription && 
              <GalleryItemBody item={ item }/>
            }
          </div>
        )

    }

  })

  return content

}

// Can not directly use useSiteData().pageSettings. Guessing that is becuase this component is rendered by showModalGallery wich is a regular function
// So translations have to be passed to this component.
const ModalGallery = ({ onClose, gallery, translations, transparentBackground }) => {

  const container = useRef()
  const windowSize = useWindowSize()
    
  // stop content behing the modal from scrolling
  useEffect(() => {
    document.body.classList.add('u-no-scroll')
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  },[])

  const handleClickOutside = event => {
    if (!container.current.contains(event.target)) {
      handleCloseModal()
    }
  }

  const handleCloseModal = () => {
    document.body.classList.remove('u-no-scroll')
    onClose()
  }

  // This is a hacky workaround but can't come up with a better solution.
  // ISSUE: after building the modal gallery we have to make sure images fit within the boundaries
  // max gallery width is 525px and the image should not be taller then the viewport
  // however it seems like we get a race condition where the DOM for the inserted image is not fully loaded
  // i.e: image.children[0] does not exist as a property until it is fully loaded
  // so this function checks that all images have image.children[0] 
  // and the function  below, fitImagesVerticallyIfLoaded, recursively calls itself until all images are loaded
  // before calling fitImagesVerticallyInBrowserWindow that does the actual calculations for scaling
  const imagesAreLoadedInDom = () => {
    const imageWrappers = container.current.querySelectorAll('.o-modal-gallery__item--image-wrapper')
    let areFullyLoaded = true
    for( let i = 0; i < imageWrappers.length ; i++ ) {
      if( !imageWrappers[i].children[0] ) {
        areFullyLoaded =  false
        break // we can break the loop since all of them need to be loaded
      }
    }
    return areFullyLoaded
  }

  const fitImagesVerticallyIfLoaded = () => {
    if( imagesAreLoadedInDom() ) {
      fitImagesVerticallyInBrowserWindow()
    } else {
      setTimeout(() => {
        fitImagesVerticallyIfLoaded()
      },10)
    }
  }

  // calculate whenever window size changes
  useEffect(()=>{    
    // i think we get a race condition for the render without the timeout and we don't have access
    // to the image-wrapper > img unless we wait a tick
    setTimeout( () => {
      fitImagesVerticallyIfLoaded()
      fitVideosVerticallyInBrowserWindow()
    },1)

  },[ windowSize.width, windowSize.height ])
  

  const fitImagesVerticallyInBrowserWindow = () => {

    const imageWrappers = container.current.querySelectorAll('.o-modal-gallery__item--image-wrapper')
    // images must fit within this height
    const maxItemHeight = windowSize.height - 48
    // max gallery width is 525 unless we are on mobile
    const galleryWidth = container.current.offsetWidth < 525 ? container.current.offsetWidth : 525

    // loop through all images and clear and set scaling if needed so they vertically fit in the window
    imageWrappers.forEach( image => {
      // grab the image from the gallery using index
      const galleryItem = gallery.items[ image.dataset.index ] 
      // calculate scaling ratio
      const ratio = galleryItem.file.details.image.height / galleryItem.file.details.image.width
      
      image.style.height = "auto"
      image.children[0].classList.remove( 'u-object-fit__media' )

      // constrain the image if needed
      if( ratio * galleryWidth > maxItemHeight ) {
        image.style.height = maxItemHeight + "px"
        image.children[0].classList.add( 'u-object-fit__media' )
      }

    })

  }

  // properly set aspect ratio padding for video using css custom var. never set aspect ratio below 16/9 (56.25)
  const fitVideosVerticallyInBrowserWindow = () => {

    const videoWrappers = container.current.querySelectorAll('.o-modal-gallery__item--video-wrapper')
    // video must fit within this height
    const maxItemHeight = windowSize.height - 48
    // max gallery width is 525 unless we are on mobile
    const galleryWidth = container.current.offsetWidth < 525 ? container.current.offsetWidth : 525
    
    videoWrappers.forEach( el => {
      let aspectRatio = el.getAttribute('lv_a')
      
      if( aspectRatio ) {

        const newHeight = galleryWidth * ( aspectRatio / 100)

        if( newHeight > maxItemHeight ) {
          aspectRatio = ( maxItemHeight / galleryWidth ) * 100
          if( aspectRatio < 56.25 ) {
            aspectRatio = 56.25
          }
        }
        
        el.style.setProperty('--videoAspectPadding', aspectRatio + '%')
      }
    })
  }

  // set information about video dimensions so we can use it to properly set aspect padding for videos
  const videoMetaDataLoaded = ( e ) => {

    if( !e.target ) return

    const videoWrapper = e.target.offsetParent.firstChild
    videoWrapper.setAttribute("lv_w", e.target.videoWidth)
    videoWrapper.setAttribute("lv_h", e.target.videoHeight)
    videoWrapper.setAttribute("lv_a", (e.target.videoHeight / e.target.videoWidth) * 100 )

    fitVideosVerticallyInBrowserWindow()
    
  }

  // build our gallery and pass a callback so we can set dimensions when a video has loaded
  const content = buildModalGalleryItems( gallery, videoMetaDataLoaded )

  return (
    <div className={ "o-modal o-modal--gallery" + (transparentBackground ? " o-modal--opaque" : "") } >
      <div className="o-modal__wrapper" >
        <button onClick={ handleCloseModal } className="o-modal__close a-btn">
          <Icon iconName="icon-cross" className="o-modal__icon a-icon a-icon--xs"/>
          <span className="u-visually-hidden">{ (translations && translations.close ? translations.close : "Close") }</span>
        </button>

        <div className="o-modal__container" id="modal">
          <div className="o-modal-gallery__items" ref={ container }>
            { gallery.title &&
              <div className="o-modal-gallery__item o-modal-gallery__item--header" key="gallery-header">
                <h4>{ gallery.title }</h4>
              </div>
            }
            { content }
          </div>
        </div>
      </div>
    </div>
  )  
}
 

// This is not a react function component so we can't use the hook useSideData() here
// And when it is used in ModalGallery it results in the error 'StaticQuery could not be fetched'
const showModalGallery = ({ gallery, translations, transparentBackground = false }) => {  
  const modalElement = document.getElementById('modal')
  ReactDOM.render( <ModalGallery onClose={ () => ReactDOM.render(null, modalElement) } gallery={gallery} translations={ translations } transparentBackground={ transparentBackground }/>, modalElement )
}

export default showModalGallery
